import { gsap } from "gsap";
import { useId, useRef } from "react";

import { MOTION_DURATION, MOTION_EASE } from "@/shared/components/motion/motion.tokens";
import { cn } from "@/shared/lib/utils";

import { useIllustrationLoop } from "./useIllustrationLoop";

/** Sample code on the letter; illustrative only, never a real code. */
const SAMPLE_CODE = "482 915";

/** How far the letter rises out of the envelope, in viewBox units. */
const LETTER_RISE = 22;

/**
 * One delivery: the closed flap folds away, the open flap rises behind, the letter with the code slides up
 * out of the envelope and holds, then everything goes back for the next round.
 */
function buildEmailLoop(svg: SVGSVGElement): gsap.core.Timeline {
  const closedFlap = svg.querySelector<SVGPathElement>("[data-flap-closed]");
  const openFlap = svg.querySelector<SVGPathElement>("[data-flap-open]");
  const letter = svg.querySelector<SVGGElement>("[data-letter]");
  const badge = svg.querySelector<SVGGElement>("[data-badge]");
  const timeline = gsap.timeline({ repeat: -1, repeatDelay: 0.6, paused: true });
  if (!closedFlap || !openFlap || !letter || !badge) return timeline;

  // The markup is the static end state (open, letter out); the loop starts from a sealed envelope.
  gsap.set(closedFlap, { scaleY: 1, opacity: 1, transformOrigin: "50% 0%" });
  gsap.set(openFlap, { scaleY: 0, transformOrigin: "50% 100%" });
  gsap.set(letter, { y: LETTER_RISE });
  gsap.set(badge, { scale: 0, opacity: 0, transformOrigin: "50% 50%" });

  timeline
    .to(badge, { scale: 1, opacity: 1, duration: MOTION_DURATION.quick * 1.4, ease: MOTION_EASE.pop }, 0.3)
    .to(closedFlap, { scaleY: 0, duration: MOTION_DURATION.quick, ease: MOTION_EASE.enter }, "+=0.35")
    .to(openFlap, { scaleY: 1, duration: MOTION_DURATION.quick, ease: MOTION_EASE.enter })
    .to(badge, { scale: 0, opacity: 0, duration: MOTION_DURATION.quick, ease: MOTION_EASE.enter }, "<")
    .to(letter, { y: 0, duration: MOTION_DURATION.enter, ease: MOTION_EASE.pop }, "-=0.05")
    .to({}, { duration: 1.4 })
    .to(letter, { y: LETTER_RISE, duration: MOTION_DURATION.quick * 1.6, ease: MOTION_EASE.enter })
    .to(openFlap, { scaleY: 0, duration: MOTION_DURATION.quick, ease: MOTION_EASE.enter })
    .to(closedFlap, { scaleY: 1, duration: MOTION_DURATION.quick, ease: MOTION_EASE.enter });
  return timeline;
}

export interface EmailCodeIllustrationProps {
  className?: string;
}

/**
 * The email backup in miniature: an envelope arrives (a small "new" mark), opens, and a letter with a
 * six-digit code slides up out of it. It makes "we send a one-time code to your email" visible without a
 * word. Decorative — the text around it already says it. Without motion, the envelope open and the code out.
 */
export function EmailCodeIllustration({ className }: EmailCodeIllustrationProps) {
  const ref = useRef<SVGSVGElement>(null);
  const clipId = `email-letter-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  useIllustrationLoop(ref, buildEmailLoop);

  return (
    <svg
      ref={ref}
      viewBox="0 0 148 76"
      className={cn("h-[76px] w-auto max-w-full text-muted-foreground", className)}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        {/* The letter is only visible above the envelope's front pocket. */}
        <clipPath id={clipId}>
          <rect x="0" y="0" width="148" height="56" />
        </clipPath>
      </defs>

      {/* Back of the envelope */}
      <rect
        x="40"
        y="30"
        width="68"
        height="40"
        rx="4"
        className="fill-card"
        stroke="currentColor"
        strokeOpacity="0.55"
        strokeWidth="1.4"
      />

      {/* The flap, open — behind the letter */}
      <path
        data-flap-open=""
        d="M40.7 30 L74 9 L107.3 30 Z"
        className="fill-card"
        stroke="currentColor"
        strokeOpacity="0.45"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />

      {/* The letter with the code */}
      <g clipPath={`url(#${clipId})`}>
        <g data-letter="">
          <rect
            x="50"
            y="22"
            width="48"
            height="30"
            rx="3"
            className="fill-card"
            stroke="currentColor"
            strokeOpacity="0.4"
            strokeWidth="1.1"
          />
          <rect x="56" y="27" width="20" height="2.4" rx="1.2" fill="currentColor" fillOpacity="0.3" />
          <text
            x="74"
            y="44"
            textAnchor="middle"
            className="fill-foreground font-geist-mono"
            fontSize="10.5"
            fontWeight="600"
            letterSpacing="0.5"
          >
            {SAMPLE_CODE}
          </text>
        </g>
      </g>

      {/* Front pocket: the lower folds that hide the bottom of the letter */}
      <path
        d="M40.7 69.3 L40.7 44 L74 58 L107.3 44 L107.3 69.3 Z"
        className="fill-card"
        stroke="currentColor"
        strokeOpacity="0.3"
        strokeWidth="1.1"
        strokeLinejoin="round"
      />

      {/* The flap, closed — in front, folded away when the envelope opens (hidden in the static end state) */}
      <path
        data-flap-closed=""
        d="M40.7 30.7 L74 52 L107.3 30.7 Z"
        className="fill-card"
        stroke="currentColor"
        strokeOpacity="0.5"
        strokeWidth="1.3"
        strokeLinejoin="round"
        opacity="0"
      />

      {/* "New mail" — the moment the code arrives */}
      <g data-badge="" opacity="0">
        <circle cx="108" cy="30" r="6.5" className="fill-primary" />
        <path
          d="M105.3 30.2 L107.3 32.2 L110.8 28.2"
          fill="none"
          className="stroke-primary-foreground"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    </svg>
  );
}
